import { useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-location';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { URLs } from '../../../constants';
import { featureFlags } from '../../../feature-flags';
import { trackingIds } from '../../../tracking-ids';
import { useFetchMessagingROIOOverview, useFetchRoiTrendLine } from '../hooks';
import { WidgetCardProps } from './types';
import { WidgetCard } from './widget-card';

export const BTWidgetCard = ({ handleUpdateActiveCards, showTrendLine, timePeriod }: WidgetCardProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.bulkTextRoiDemoData);

  const payload = {
    additionalRequestPayload: {
      roiType: RoiType.MARKETING_MESSAGE,
    },
    filters: {
      startDate: timePeriod.value.startDate,
      endDate: timePeriod.value.endDate,
      locationIds: selectedLocationIds,
    },
    isDemoAccount,
  };

  const { aggregated, isLoading } = useFetchMessagingROIOOverview(payload);

  const appointmentsScheduled = aggregated?.patientsScheduled?.appointmentsCount || 0;

  const { isLoading: isLoadingTrendLine, trendLine } = useFetchRoiTrendLine({
    ...payload,
    isEnabled: showTrendLine,
  });

  const handleNavigate = useCallback(() => {
    navigate({
      to: URLs.ROI_BULK_TEXT,
    });
  }, []);

  useEffect(() => {
    handleUpdateActiveCards('bulkText', !!appointmentsScheduled);
  }, [appointmentsScheduled]);

  if (!appointmentsScheduled) {
    return null;
  }

  return (
    <WidgetCard
      backgroundConfig={{
        gradientStart: theme.colors.secondary.eggplant40,
        gradientEnd: theme.colors.secondary.eggplant30,
      }}
      iconName='bulk-messaging-small'
      isLoading={isLoading || isLoadingTrendLine}
      onClick={handleNavigate}
      subTitle={t('Appointments Scheduled')}
      title={t('Bulk Texting')}
      trackingId={trackingIds.roi.bulkTextRoiWidget}
      trend={showTrendLine && trendLine}
      value={formatters.value(appointmentsScheduled)}
    />
  );
};
